import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { isMissingColumnError, MARKETING_EXCLUDED_COLUMN, MARKETING_EXCLUSION_SOURCE_REF } from "@/utils/contacts";

/** The store's marketing exclusions, read under RLS from sms_contacts. */
export interface MarketingExclusions {
  /** False until migration 20260917100000_sms_marketing_exclusion.sql is applied:
   *  nobody can be excluded yet, so the Contacts actions stay hidden. */
  available: boolean;
  /** customer_phone → marketing_excluded_at. */
  byPhone: Map<string, string>;
}

const PAGE_SIZE = 500;

/**
 * Every customer the owner excluded from marketing texts. The customer views do
 * not carry the flag, so the roster, its "Excluded" filter and the campaign
 * composer's audience all read it here. A missing column (migration not yet
 * applied) reads as "nobody excluded, feature unavailable"; any other error throws.
 */
export async function fetchMarketingExclusions(sb: SupabaseClient): Promise<MarketingExclusions> {
  const byPhone = new Map<string, string>();
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await sb
      .from("sms_contacts")
      .select(`customer_phone, ${MARKETING_EXCLUDED_COLUMN}`)
      .not(MARKETING_EXCLUDED_COLUMN, "is", null)
      .order("customer_phone", { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) {
      if (isMissingColumnError(error, MARKETING_EXCLUDED_COLUMN)) return { available: false, byPhone };
      throw error;
    }
    const page = (data ?? []) as unknown as { customer_phone: string; marketing_excluded_at: string }[];
    for (const row of page) byPhone.set(row.customer_phone, row.marketing_excluded_at);
    if (page.length < PAGE_SIZE) break;
  }
  return { available: true, byPhone };
}

/**
 * Every phone whose contact row exists only because the owner excluded them
 * (sms_set_marketing_exclusion() creates it with source manual, source_ref
 * marketing_exclusion). Kept after an undo, since the row stays. The roster's
 * source filters use it so they match describeSource.
 */
export async function fetchExclusionCreatedPhones(sb: SupabaseClient): Promise<string[]> {
  const phones: string[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await sb
      .from("sms_contacts")
      .select("customer_phone")
      .eq("source", "manual")
      .eq("source_ref", MARKETING_EXCLUSION_SOURCE_REF)
      .order("customer_phone", { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const page = (data ?? []) as unknown as { customer_phone: string }[];
    for (const row of page) phones.push(row.customer_phone);
    if (page.length < PAGE_SIZE) break;
  }
  return phones;
}

export interface SetMarketingExclusionInput {
  customer_phone: string;
  excluded: boolean;
}

export interface SetMarketingExclusionResult {
  ok: true;
  customer_phone: string;
  excluded: boolean;
  excluded_at: string | null;
  changed: boolean;
}

/**
 * Sets or clears one customer's marketing exclusion through sms-actions'
 * `marketing_exclusion` action (the store and actor come from the caller's JWT
 * on the server), then refreshes every count the exclusion moves: the roster
 * and its tiles, the Invitation forecast and backlog count, and the campaign
 * composer's audience.
 */
export const useSetMarketingExclusion = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation<SetMarketingExclusionResult, Error, SetMarketingExclusionInput>({
    mutationFn: async (input) => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("sms-actions", {
        body: { action: "marketing_exclusion", customer_phone: input.customer_phone, excluded: input.excluded },
      });
      if (error) {
        // A 4xx carries the action's plain-language reason in its JSON body.
        const context = (error as { context?: { json?: () => Promise<unknown> } }).context;
        const body = context?.json ? await context.json().catch(() => null) : null;
        const reason = (body as { error?: unknown } | null)?.error;
        throw new Error(typeof reason === "string" ? reason : "Could not save the change. Try again.");
      }
      const result = data as SetMarketingExclusionResult | { ok: false; error?: string } | null;
      if (!result || result.ok !== true) {
        throw new Error((result as { error?: string } | null)?.error ?? "Could not save the change. Try again.");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["sms-invite-forecast"] });
      queryClient.invalidateQueries({ queryKey: ["sms-backlog-count"] });
      queryClient.invalidateQueries({ queryKey: ["sms-contact-counts"] });
      queryClient.invalidateQueries({ queryKey: ["sms-campaign-audience-breakdown"] });
      queryClient.invalidateQueries({ queryKey: ["sms-campaign-audience-estimate"] });
    },
  });
};
